import React, { useState, useEffect } from "react";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";

import config from "../config.js";
import { useSelector } from "react-redux";
import Layout from "../components/layout/InvoiceLayout.jsx";
import InvoiceCard from "../components/InvoiceCard.jsx";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";

const InvoiceManagementPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { API_BASE_URL } = config;
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/getInvoiceBykyc/${selectedCompanyId}`
        );
        const data = await response.json();
        setInvoices(data.data || []);
      } catch (error) {
        console.error("Error fetching invoices:", error);
        setSnackbarInfo({
          open: true,
          message: "Failed to fetch invoices",
          severity: "error",
        });
      }
    };

    fetchInvoices();
  }, [API_BASE_URL, selectedCompanyId]);

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/downloadInvoicePDF/${invoice._id}`
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_${invoice.invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setSnackbarInfo({
        open: true,
        message: "Invoice downloaded successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error downloading PDF:", error);
      setSnackbarInfo({
        open: true,
        message: "Failed to download invoice",
        severity: "error",
      });
    }
  };

  const handleEditInvoice = (invoice) => {
    
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/v1/deleteinvoices/${selectedInvoice.invoiceId}`
      );

      setInvoices(
        invoices.filter((inv) => inv.invoiceId !== selectedInvoice.invoiceId)
      );
      setSnackbarInfo({
        open: true,
        message: "Invoice deleted successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting invoice:", error);
      setSnackbarInfo({
        open: true,
        message: "Failed to delete invoice",
        severity: "error",
      });
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const handleShareInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setOpenShareDialog(true);
  };

  const handleSendShare = async (method) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/shareInvoice/${selectedInvoice._id}`,
        {
          method: "POST",
          body: JSON.stringify({ method }),
        }
      );

      if (response.ok) {
        setSnackbarInfo({
          open: true,
          message: `Invoice shared via ${method} successfully`,
          severity: "success",
        });
      } else {
        throw new Error("Share failed");
      }
    } catch (error) {
      console.error("Error sharing invoice:", error);
      setSnackbarInfo({
        open: true,
        message: "Failed to share invoice",
        severity: "error",
      });
    } finally {
      setOpenShareDialog(false);
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <Grid container spacing={2}>
          {invoices.map((invoice) => (
            <Grid item xs={12} sm={12} md={12} key={invoice._id}>
              <InvoiceCard
                invoice={invoice}
                onDownloadPDF={handleDownloadPDF}
                onEditInvoice={() => {
                  setSelectedInvoice(invoice);
                  handleEditInvoice(invoice);
                }}
                onDeleteInvoice={() => {
                  setSelectedInvoice(invoice);
                  setOpenDeleteDialog(true);
                }}
                onShareInvoice={handleShareInvoice}
              />
            </Grid>
          ))}
        </Grid>

        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          closeIcon={true}
        >
          <DialogTitle>Delete Invoice</DialogTitle>
          <DialogContent>
            Are you sure you want to delete invoice {selectedInvoice?.invoiceId}
            ?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDeleteInvoice} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openShareDialog}
          onClose={() => setOpenShareDialog(false)}
          closeIcon={true}
        >
          <DialogTitle>Share Invoice</DialogTitle>
          <DialogContent sx={{display:"flex",gap:"4px"}}>
            <Button
              sx={{
                backgroundColor: "red",
                color: "white",
                "&:hover": {
                  backgroundColor: "darkred",
                },
              }}
              onClick={() => handleSendShare("email")}
            >
              Email <EmailIcon />
            </Button>
            <Button
              sx={{
                backgroundColor: "red",
                color: "white",
                "&:hover": {
                  backgroundColor: "darkred",
                },
              }}
              onClick={() => handleSendShare("whatsapp")}
            >
              WhatsApp <WhatsAppIcon />
            </Button>
            <Button
              sx={{
                backgroundColor: "red",
                color: "white",
                "&:hover": {
                  backgroundColor: "darkred",
                },
              }}
              onClick={() => handleSendShare("PhoneNumber")}
            >
              PhoneNumber <LocalPhoneIcon />
            </Button>
          </DialogContent>
        </Dialog>

        <Snackbar
          open={snackbarInfo.open}
          autoHideDuration={6000}
          onClose={() => setSnackbarInfo({ ...snackbarInfo, open: false })}
        >
          <Alert
            onClose={() => setSnackbarInfo({ ...snackbarInfo, open: false })}
            severity={snackbarInfo.severity}
          >
            {snackbarInfo.message}
          </Alert>
        </Snackbar>
      </div>
    </Layout>
  );
};

export default InvoiceManagementPage;
